import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { publicApi } from "../../admin/api";
import { resolveImage } from "../../lib/data";

export default function RigenerazioneSection() {
  const ref = useRef(null);
  const [stories, setStories] = useState([]);

  useEffect(() => {
    publicApi
      .get("/before-after")
      .then((r) => setStories(r.data || []))
      .catch(() => setStories([]));
  }, []);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  if (!stories.length) return null;

  return (
    <section
      ref={ref}
      data-testid="rigenerazione-section"
      className="relative py-24 md:py-36 overflow-hidden bg-[#050506]"
    >
      <motion.div
        style={{ y: bgY }}
        className="absolute inset-0 opacity-20 pointer-events-none"
      >
        <div className="absolute -top-40 left-1/4 h-[500px] w-[500px] rounded-full bg-[#C48F56] blur-[160px]" />
        <div className="absolute -bottom-20 right-1/3 h-[400px] w-[400px] rounded-full bg-[#70142A] blur-[140px]" />
      </motion.div>

      <div className="relative px-6 md:px-12 max-w-[1440px] mx-auto">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50">
              <span className="h-px w-8 bg-[#C48F56]" />
              Rigenerazione urbana
            </div>
            <h2 className="mt-5 font-display text-5xl md:text-6xl lg:text-7xl tracking-tightest">
              Prima. <span className="text-[#C48F56]">Dopo.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-10">
            <p className="text-white/70 text-lg leading-relaxed">
              Milano cambia, cresce, si rinnova. Trasformiamo edifici esistenti
              in spazi di accoglienza moderna. Ogni hotel racconta una storia di
              rigenerazione: da volumi dimenticati a luoghi che oggi arricchiscono
              la città.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stories.map((s) => (
            <BeforeAfterCard key={s.id || s.slug} story={s} />
          ))}
        </div>

        <p className="mt-10 text-center text-[11px] font-mono uppercase tracking-[0.25em] text-white/30">
          Trascina il cursore per confrontare
        </p>
      </div>
    </section>
  );
}

function BeforeAfterCard({ story }) {
  const [progress, setProgress] = useState(50);
  const beforeSrc = resolveImage(story.before_img);
  const afterSrc = resolveImage(story.after_img);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.05 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="group relative rounded-2xl overflow-hidden border border-white/10 bg-black"
      data-testid={`before-after-${story.slug}`}
    >
      <div className="relative aspect-[4/5] select-none">
        {/* AFTER base */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${afterSrc})` }}
        />
        {/* BEFORE overlay clipped */}
        <div
          className="absolute inset-y-0 left-0 overflow-hidden"
          style={{ width: `${progress}%` }}
        >
          <div
            className="absolute inset-0 bg-cover bg-center grayscale contrast-[0.85] brightness-[0.85]"
            style={{
              backgroundImage: `url(${beforeSrc})`,
              width: `${(100 / Math.max(progress, 1)) * 100}%`,
            }}
          />
        </div>

        {/* divider line + handle */}
        <div
          className="absolute inset-y-0 w-[2px] bg-white pointer-events-none shadow-[0_0_20px_rgba(196,143,86,0.4)]"
          style={{ left: `${progress}%` }}
        >
          <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-10 w-10 rounded-full bg-white text-black grid place-items-center text-xs font-semibold shadow-lg">
            ⇆
          </div>
        </div>

        {/* slider */}
        <input
          type="range"
          min={0}
          max={100}
          value={progress}
          onChange={(e) => setProgress(Number(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
          aria-label={`Confronta prima e dopo ${story.name}`}
          data-testid={`slider-${story.slug}`}
        />

        <div className="absolute top-4 left-4 glass rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-white">
          Prima
        </div>
        <div className="absolute top-4 right-4 glass rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#C48F56]">
          Dopo
        </div>
      </div>

      <div className="p-6">
        <h3 className="font-display text-2xl tracking-tight">{story.name}</h3>
        <div className="mt-3 space-y-1.5 text-sm">
          {story.before_caption && (
            <p className="text-white/40">
              <span className="text-white/60 font-medium">Prima: </span>
              {story.before_caption}
            </p>
          )}
          {story.after_caption && (
            <p className="text-white/70">
              <span className="text-[#C48F56] font-medium">Oggi: </span>
              {story.after_caption}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
